import { type FC, memo, useMemo } from 'react';

import { prop } from '@ipfabric/utils/fp/prop';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { PathLookupNodeColors } from 'components/Graph/GraphSidePanel/PathLookupNodeColors/PathLookupNodeColors';
import type { PathLookupVisualizationSetupTabId } from 'components/Graph/GraphSidePanelData';
import { showSidePanel } from 'components/Graph/commands/showSidePanel';
import { selectPathLookupApplied } from 'components/Graph/model/selectors/graphMetadata/selectPathLookupApplied';
import { TabView } from 'components/Tabs/TabView';
import { useLocale } from 'containers/App/LocaleContext';
import type { PathLookupDataInputHook } from '../GraphPathlookup/usePathLookupDataInput';
import { PathLookupEdgeColors } from '../PathLookupEdgeColors/PathLookupEdgeColors';
import { PathLookupApplySettings } from './PathLookupApplySettings';

interface Props {
  pathLookupDataInputHook: PathLookupDataInputHook;
}

const BarePathLookupVisualizationSetup: FC<Props> = (props) => {
  const msgVisualizationSetup =
    useLocale().canvasGraph.graphSidePanel.pathlookup.visualizationSetup;

  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    pathLookupApplied: selectPathLookupApplied,
    tabId: prop('sidePanel', 'pathLookupVisualizationSetupTabId'),
    dataInputTabId: prop('sidePanel', 'pathLookupDataInputTabId'),
  });

  const dataInputFormik = (() => {
    switch (ctxProps.dataInputTabId) {
      case 'hostToDefaultGW':
        return props.pathLookupDataInputHook.hostToDefaultGWFormik;
      case 'multicast':
        return props.pathLookupDataInputHook.multicastFormik;
      default:
        return props.pathLookupDataInputHook.unicastFormik;
    }
  })();

  const tabs = useMemo(
    () =>
      [
        {
          key: 'edgeColors',
          label: msgVisualizationSetup.tabs.edgeColors,

          render: () => <PathLookupEdgeColors />,
        },
        {
          key: 'nodeColors',
          label: msgVisualizationSetup.tabs.nodeColors,

          render: () => <PathLookupNodeColors />,
        },
      ] as const,
    [msgVisualizationSetup],
  );

  return (
    <div className='flex flex-1 flex-col overflow-hidden'>
      <TabView<PathLookupVisualizationSetupTabId>
        tabs={tabs}
        activeTab={ctxProps.tabId}
        onChangeTab={(pathLookupVisualizationSetupTabId) => {
          ctxProps.dispatch(showSidePanel, {
            pathLookupVisualizationSetupTabId,
          });
        }}
        stretch
        contentClassName='graph-side-panel-inner-tab flex flex-col'
        className='tabs-underline'
        variant='legacy'
      />
      <PathLookupApplySettings dataInputFormik={dataInputFormik} />
    </div>
  );
};

export const PathLookupVisualizationSetup = memo(
  BarePathLookupVisualizationSetup,
);
